import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Building2, Search, Plus, Send } from "lucide-react";
import { COUNTERPARTIES_TESTIDS as T } from "@/constants/testIds";

const STATUS_LABEL = {
  connected: { label: "Подключен к ЭДО", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  invited: { label: "Приглашение отправлено", color: "bg-amber-50 text-amber-700 border-amber-200" },
  not_connected: { label: "Не подключен", color: "bg-slate-100 text-slate-500 border-slate-200" },
};

export default function Counterparties() {
  const [list, setList] = useState([]);
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", inn: "", kpp: "" });

  const load = async () => {
    const { data } = await api.get("/counterparties", { params: search ? { search } : {} });
    setList(data);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const addCounterparty = async () => {
    if (!form.name || !form.inn) {
      toast.error("Укажите название и ИНН");
      return;
    }
    try {
      await api.post("/counterparties", form);
      toast.success("Контрагент добавлен");
      setForm({ name: "", inn: "", kpp: "" });
      setShowAdd(false);
      load();
    } catch {
      toast.error("Не удалось добавить контрагента");
    }
  };

  const invite = async (id) => {
    try {
      await api.post(`/counterparties/${id}/invite`);
      toast.success("Приглашение отправлено контрагенту");
      load();
    } catch {
      toast.error("Ошибка отправки приглашения");
    }
  };

  return (
    <div data-testid="counterparties-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Контрагенты</h1>
          <p className="text-sm text-slate-500 mt-0.5">{list.length} компаний в базе</p>
        </div>
        <Button onClick={() => setShowAdd((v) => !v)} data-testid={T.addButton} className="bg-slate-900 hover:bg-slate-800">
          <Plus className="w-4 h-4 mr-1.5" />Добавить контрагента
        </Button>
      </div>

      {showAdd && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 grid grid-cols-3 gap-3">
          <Input data-testid={T.nameInput} placeholder="Название организации" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Input data-testid={T.innInput} placeholder="ИНН" value={form.inn} onChange={(e) => setForm({ ...form, inn: e.target.value })} />
          <Button onClick={addCounterparty} data-testid={T.saveButton} className="bg-blue-600 hover:bg-blue-700">Сохранить</Button>
        </div>
      )}

      <div className="relative max-w-sm mb-4">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input data-testid={T.searchInput} placeholder="Поиск по названию или ИНН" className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((cp) => {
          const s = STATUS_LABEL[cp.status];
          return (
            <div key={cp.id} data-testid={`counterparty-card-${cp.id}`} className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md transition-shadow">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center">
                  <Building2 className="w-4 h-4 text-slate-500" />
                </div>
                <div>
                  <div className="font-medium text-sm text-slate-900">{cp.name}</div>
                  <div className="text-xs text-slate-400 font-mono">ИНН {cp.inn}</div>
                </div>
              </div>
              <span className={`text-xs px-2 py-1 rounded-full border ${s.color}`}>{s.label}</span>
              {cp.status === "not_connected" && (
                <Button size="sm" variant="outline" className="w-full mt-3" onClick={() => invite(cp.id)} data-testid={`invite-counterparty-button-${cp.id}`}>
                  <Send className="w-3.5 h-3.5 mr-1.5" />Отправить приглашение
                </Button>
              )}
            </div>
          );
        })}
      </div>
      {list.length === 0 && <div className="text-center text-slate-400 text-sm py-16" data-testid="counterparties-empty-state">Контрагенты не найдены</div>}
    </div>
  );
}
